import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import { checkTemplateDeploymentAccess } from "shared/Stack/access/helpers/checkTemplateDeploymentAccess";
import { Action } from "types/generated";
import { AccessResult, checkAllowedAction } from "utils/access";

import { StackContext } from "../Context";

type StackSchedulingAccess = {
  canCreateTask: AccessResult;
  canUpdateTask: AccessResult;
  canDeleteTask: AccessResult;
  canCreateRun: AccessResult;
  canUpdateRun: AccessResult;
  canDeleteRun: AccessResult;
  canCreateDelete: AccessResult;
  canUpdateDelete: AccessResult;
  canDeleteDelete: AccessResult;
};

/**
 * Access checks for the stack scheduling view, one per scheduled action.
 * Backed by the stack's `allowedActions`, so a custom role that was granted a
 * specific STACK_SCHEDULED_* permission can use it without needing Space Admin.
 * Template-deployment stacks stay locked, matching the rest of the stack UI.
 */
const useStackSchedulingAccess = (): StackSchedulingAccess => {
  const { stack } = useTypedContext(StackContext);

  return useMemo(() => {
    const check = (action: Action): AccessResult =>
      checkTemplateDeploymentAccess(stack) ?? checkAllowedAction(stack, action);

    return {
      canCreateTask: check(Action.StackScheduledTaskCreate),
      canUpdateTask: check(Action.StackScheduledTaskUpdate),
      canDeleteTask: check(Action.StackScheduledTaskDelete),
      canCreateRun: check(Action.StackScheduledRunCreate),
      canUpdateRun: check(Action.StackScheduledRunUpdate),
      canDeleteRun: check(Action.StackScheduledRunDelete),
      canCreateDelete: check(Action.StackScheduledDeleteCreate),
      canUpdateDelete: check(Action.StackScheduledDeleteUpdate),
      canDeleteDelete: check(Action.StackScheduledDeleteDelete),
    };
  }, [stack]);
};

export default useStackSchedulingAccess;
